import { BaseThemeService, IThemeModel } from '@adaptiva/core/themes';
import { BaseEditorComponent, BaseEditorError, BUILT_IN_THEMES, EditorInput, EEditorErrorType, EJavaClassNames, Theme } from '@adaptiva/data-service';
import { FileUploaderComponent } from '@adaptiva/widgets/file-uploader';
import { InputComponent } from '@adaptiva/widgets/input';
import { ThemeDisplayComponent } from '@adaptiva/widgets/themes';
import { ChangeDetectionStrategy, Component, forwardRef, inject, ViewChild } from '@angular/core';
import { Observable, tap } from 'rxjs';

@Component({
	standalone: false,
	selector: 'ada-themes-editor-area',
	templateUrl: './themes-editor-area.component.html',
	styleUrls: ['./themes-editor-area.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: BaseEditorComponent,
			useExisting: forwardRef(() => ThemesEditorAreaComponent)
		}
	]
})
export class ThemesEditorAreaComponent extends BaseEditorComponent<Theme> {

	@ViewChild(ThemeDisplayComponent)
	protected themeDisplay: ThemeDisplayComponent;

	@ViewChild('NameInput')
	public nameInput: InputComponent;

	public override readonly className: EJavaClassNames = EJavaClassNames.Theme;

	public override tabDescription: string = 'Choose a new theme or create your own to make Adaptiva Admin Portal look the way you like';

	public override tabTitle: string = 'Themes';

	public readonly acceptedFileTypes: string[] = ['.png', '.ico', '.svg'];

	public readonly acceptedFaviconTypes: string[] = ['.png'];

	public readonly maxLogoSize: int = Math.pow(1024 * 2, 2) / 4 * 3; // 2MB max icon size

	public readonly themeService: BaseThemeService = inject(BaseThemeService);

	public override generateNewObject(_input: EditorInput<Theme>): Observable<Theme> {
		return super.generateNewObject(_input).pipe(
			tap((_theme: Theme) => {
				const _randomizedTheme: IThemeModel = this.themeService.randomTheme();

				_theme.foregroundColor = _randomizedTheme.foregroundColor;
				_theme.backgroundColor = _randomizedTheme.backgroundColor;
				_theme.primaryColor = _randomizedTheme.primaryColor;
				_theme.accentColor = _randomizedTheme.accentColor;
				_theme.onPrimaryColor = _randomizedTheme.onPrimaryColor;
				_theme.onAccentColor = _randomizedTheme.onAccentColor;
				_theme.successColor = _randomizedTheme.successColor;
				_theme.onSuccessColor = _randomizedTheme.onSuccessColor;
				_theme.warningColor = _randomizedTheme.warningColor;
				_theme.onWarningColor = _randomizedTheme.onWarningColor;
				_theme.errorColor = _randomizedTheme.errorColor;
				_theme.onErrorColor = _randomizedTheme.onErrorColor;

				// inherit branding elements from default theme
				_theme.logo = this.themeService.defaultTheme.logo;
				_theme.onPrimaryLogo = this.themeService.defaultTheme.onPrimaryLogo;
				_theme.favicon = this.themeService.defaultTheme.favicon;
				_theme.headerColor = this.themeService.defaultTheme.headerColor;
				_theme.onHeaderColor = this.themeService.defaultTheme.onHeaderColor;
			})
		);
	}

	public override beforeSave(): Observable<Theme> {
		this.state.set('isBuilt', false);

		return super.beforeSave();
	}

	public override commitModelToView(): void {
		this.themeDisplay?.updateThemeTemplate(this.state.value);
	}

	public apply(): void {
		this.themeService.applyTheme(this.state.value);
	}

	public setName(value: string): void {
		this.state.set('name', value);
	}

	public setColor(event: { color: keyof Theme, rgb: number[]; }): void {
		if (this.state.value)
			this.state.set(event.color, event.rgb);
	}

	public setCustomLogo(_event: FileUploaderComponent.IContent): void {
		this.state.set('logo', _event.stringContent?.[0] || null);
	}

	public setOnPrimaryLogo(_event: FileUploaderComponent.IContent): void {
		this.state.set('onPrimaryLogo', _event.stringContent?.[0] || null);
	}

	public setFavicon(_event: FileUploaderComponent.IContent): void {
		this.state.set('favicon', _event.stringContent?.[0] || null);
	}

	protected override validate(): BaseEditorError[] {
		const _errors: BaseEditorError[] = [];
		const _themeNameError: string = this._validateThemeName(this.state.value.name);
		const _colorsError: string = this._validateColors();

		if (_themeNameError) {
			_errors.push(new BaseEditorError(
				EEditorErrorType.Error,
				this.nameInput.guid,
				['Name', _themeNameError]
			));
		}

		if (_colorsError) {
			_errors.push(new BaseEditorError(
				EEditorErrorType.Error,
				this.themeDisplay.guid,
				['Colors', _colorsError]
			));
		}

		return _errors;
	}

	private _validateThemeName(_name: string): string {
		if (!_name)
			return 'Theme name required';

		if (BUILT_IN_THEMES.has(_name))
			return `Cannot use reserved theme name: ${_name}`;

		const _pattern: RegExp = /^[a-zA-Z_$][0-9a-zA-Z_$ ]*$/g;

		if (!_pattern.test(_name))
			return 'Invalid theme name. Cannot contain special characters.';
	}

	private _validateColors(): string {
		const _colorNames: string[] = [
			'foreground',
			'background',
			'primary',
			'accent',
			'success',
			'warning',
			'error',
			'onPrimary',
			'onAccent',
			'onSuccess',
			'onWarning',
			'onError',
			'header',
			'onHeader',
		];

		const _invalidColors: string[] = _colorNames.filter((_name: string) => {
			const _key: string = `${_name}Color`;

			return !this.state.value[_key] || this.state.value[_key].length !== 3;
		});

		return _invalidColors.length ? `Invalid configuration for ${_invalidColors.join(', ')}` : '';
	}

}
